// ===== 천간 / 지지 / 오행 기본 데이터 =====
export const HEAVENLY_STEMS = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
export const HEAVENLY_STEMS_KOR = ['갑','을','병','정','무','기','경','신','임','계'];
export const EARTHLY_BRANCHES = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
export const EARTHLY_BRANCHES_KOR = ['자','축','인','묘','진','사','오','미','신','유','술','해'];
export const BRANCH_TIME_NAMES = ['자시','축시','인시','묘시','진시','사시','오시','미시','신시','유시','술시','해시'];

export type Element = '목' | '화' | '토' | '금' | '수';

// 천간 오행
export const STEM_ELEMENTS: Element[] = ['목','목','화','화','토','토','금','금','수','수'];
// 지지 오행
export const BRANCH_ELEMENTS: Element[] = ['수','토','목','목','토','화','화','토','금','금','토','수'];

export const ELEMENT_COLORS: Record<Element, string> = {
  목: '#15803D',
  화: '#DC2626',
  토: '#B45309',
  금: '#6B7280',
  수: '#1E3A5F',
};
export const ELEMENT_BG_COLORS: Record<Element, string> = {
  목: '#DCFCE7',
  화: '#FEE2E2',
  토: '#FEF3C7',
  금: '#F3F4F6',
  수: '#E2E8F0',
};

// ===== 한국 역사적 표준시 오프셋 (분 단위) =====
export function getHistoricalTimezoneOffset(year: number, month: number, day: number): number {
  const dateVal = year * 10000 + month * 100 + day;
  if (dateVal < 19080401) return 510; // UTC+8:30
  if (dateVal < 19120101) return 510; // UTC+8:30
  if (dateVal < 19540321) return 540; // UTC+9:00 (일제강점기)
  if (dateVal < 19610810) return 510; // UTC+8:30 (환원)
  return 540; // UTC+9:00 (현재)
}

// 표준시 기준 경도 (분 단위로 차이 계산)
export function getStandardMeridian(year: number, month: number, day: number): number {
  const offset = getHistoricalTimezoneOffset(year, month, day);
  return offset === 510 ? 127.5 : 135.0;
}

// ===== 한국 서머타임(DST) 이력 =====
const DST_PERIODS = [
  { year: 1948, sm: 5, sd: 1, em: 9, ed: 13 },
  { year: 1949, sm: 4, sd: 3, em: 9, ed: 11 },
  { year: 1950, sm: 4, sd: 1, em: 9, ed: 10 },
  { year: 1951, sm: 5, sd: 6, em: 9, ed: 9 },
  { year: 1955, sm: 5, sd: 5, em: 9, ed: 8 },
  { year: 1956, sm: 5, sd: 20, em: 9, ed: 30 },
  { year: 1957, sm: 5, sd: 5, em: 9, ed: 22 },
  { year: 1958, sm: 5, sd: 4, em: 9, ed: 21 },
  { year: 1959, sm: 5, sd: 3, em: 9, ed: 20 },
  { year: 1960, sm: 5, sd: 1, em: 9, ed: 18 },
  { year: 1987, sm: 5, sd: 10, em: 10, ed: 11 },
  { year: 1988, sm: 5, sd: 8, em: 10, ed: 9 },
];

export function isDSTActive(year: number, month: number, day: number): boolean {
  const period = DST_PERIODS.find(p => p.year === year);
  if (!period) return false;
  const dateVal = month * 100 + day;
  const startVal = period.sm * 100 + period.sd;
  const endVal = period.em * 100 + period.ed;
  return dateVal >= startVal && dateVal <= endVal;
}

export function getDSTYear(year: number): typeof DST_PERIODS[number] | undefined {
  return DST_PERIODS.find(p => p.year === year);
}

// ===== 균시차 (Equation of Time) 계산 (분 단위) =====
export function getEquationOfTime(year: number, month: number, day: number): number {
  const dayOfYear = getDayOfYear(year, month, day);
  const B = (2 * Math.PI / 365) * (dayOfYear - 81);
  return 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
}

function getDayOfYear(year: number, month: number, day: number): number {
  const monthDays = [0,31,28,31,30,31,30,31,31,30,31,30,31];
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  if (isLeap) monthDays[2] = 29;
  let doy = day;
  for (let m = 1; m < month; m++) doy += monthDays[m];
  return doy;
}

// ===== 경도 보정 (분 단위) =====
export function getLongitudeCorrection(longitude: number, year: number, month: number, day: number): number {
  const standardMeridian = getStandardMeridian(year, month, day);
  return (longitude - standardMeridian) * 4;
}

// ===== 전체 시간 보정 계산 =====
export interface CorrectionResult {
  inputHour: number;
  inputMinute: number;
  longitudeCorrection: number;
  eotCorrection: number;
  dstCorrection: number;
  timezoneNote: string;
  totalCorrectionMinutes: number;
  correctedHour: number;
  correctedMinute: number;
  inputBranch: string;
  correctedBranch: string;
  branchChanged: boolean;
  dstActive: boolean;
}

export function calculateCorrection(
  year: number, month: number, day: number,
  hour: number, minute: number,
  longitude: number
): CorrectionResult {
  const longitudeCorrection = getLongitudeCorrection(longitude, year, month, day);
  const eotCorrection = getEquationOfTime(year, month, day);
  const dstActive = isDSTActive(year, month, day);
  const dstCorrection = dstActive ? -60 : 0;

  const roundedEot = Math.round(eotCorrection * 10) / 10;
  const roundedLong = Math.round(longitudeCorrection * 10) / 10;

  const totalMins = roundedLong + roundedEot + dstCorrection;
  
  const totalInputMins = hour * 60 + minute;
  let totalCorrectedMins = totalInputMins + totalMins;
  
  // 하루 범위 내로 정규화
  totalCorrectedMins = ((totalCorrectedMins % 1440) + 1440) % 1440;
  
  const correctedHour = Math.floor(totalCorrectedMins / 60);
  const correctedMinute = Math.floor(totalCorrectedMins % 60);

  const inputBranch = getTimeBranch(hour, minute);
  const correctedBranch = getTimeBranch(correctedHour, correctedMinute);

  const historicalOffset = getHistoricalTimezoneOffset(year, month, day);
  const timezoneNote = historicalOffset === 510 
    ? `${year}년 출생: 당시 한국 표준시 UTC+8:30 (동경 127.5도) 적용`
    : `${year}년 출생: 한국 표준시 UTC+9:00 (동경 135도) 적용`;

  return {
    inputHour: hour,
    inputMinute: minute,
    longitudeCorrection: Math.round(roundedLong),
    eotCorrection: Math.round(roundedEot),
    dstCorrection,
    timezoneNote,
    totalCorrectionMinutes: Math.round(totalMins),
    correctedHour,
    correctedMinute,
    inputBranch,
    correctedBranch,
    branchChanged: inputBranch !== correctedBranch,
    dstActive,
  };
}

export function getTimeBranch(hour: number, _minute: number): string {
  if (hour >= 23 || hour < 1) return '자시(子時)';
  if (hour < 3) return '축시(丑時)';
  if (hour < 5) return '인시(寅時)';
  if (hour < 7) return '묘시(卯時)';
  if (hour < 9) return '진시(辰時)';
  if (hour < 11) return '사시(巳時)';
  if (hour < 13) return '오시(午時)';
  if (hour < 15) return '미시(未時)';
  if (hour < 17) return '신시(申時)';
  if (hour < 19) return '유시(酉時)';
  if (hour < 21) return '술시(戌時)';
  return '해시(亥時)';
}

// ===== 오행 분포 계산 =====
export function getElementDistribution(saju: SajuResult): Record<Element, number> {
  const dist: Record<Element, number> = { 목:0, 화:0, 토:0, 금:0, 수:0 };
  const pillars = [saju.yearPillar, saju.monthPillar, saju.dayPillar, saju.hourPillar].filter(Boolean);
  for (const p of pillars) {
    if (!p) continue;
    dist[STEM_ELEMENTS[p.stemIdx]]++;
    dist[BRANCH_ELEMENTS[p.branchIdx]]++;
  }
  return dist;
}

// ===== 사주 팔자 계산 =====
export interface SajuPillar {
  stemIdx: number;
  branchIdx: number;
  stem: string;
  branch: string;
  stemKor: string;
  branchKor: string;
  stemElement: Element;
  branchElement: Element;
  label: string;
}

export interface SajuResult {
  yearPillar: SajuPillar;
  monthPillar: SajuPillar;
  dayPillar: SajuPillar;
  hourPillar: SajuPillar | null;
  name: string;
}

function makePillar(stemIdx: number, branchIdx: number, label: string): SajuPillar {
  const si = ((stemIdx % 10) + 10) % 10;
  const bi = ((branchIdx % 12) + 12) % 12;
  return {
    stemIdx: si, branchIdx: bi,
    stem: HEAVENLY_STEMS[si],
    branch: EARTHLY_BRANCHES[bi],
    stemKor: HEAVENLY_STEMS_KOR[si],
    branchKor: EARTHLY_BRANCHES_KOR[bi],
    stemElement: STEM_ELEMENTS[si],
    branchElement: BRANCH_ELEMENTS[bi],
    label,
  };
}

// ===== 야자시 처리: 전날 23시~24시는 다음날 일주로 계산 =====
// 사주에서 일주는 자시(23:00)부터 시작. 양력 날짜 23:00~24:00 = 다음날 일주
export function adjustForYaJasi(year: number, month: number, day: number, hour: number, useYaJasi: boolean = true): { year: number; month: number; day: number; hour: number } {
  if (!useYaJasi) return { year, month, day, hour };
  // 23시(밤 11시) 이후는 다음날 일주로 처리
  if (hour >= 23) {
    const nextDay = new Date(year, month - 1, day + 1);
    return {
      year: nextDay.getFullYear(),
      month: nextDay.getMonth() + 1,
      day: nextDay.getDate(),
      hour: hour,
    };
  }
  return { year, month, day, hour };
}

// ===== 절기 교입 시각 DB (KST 기준) =====
// 12절기 순서: 소한(1월), 입춘(2월), 경칩(3월), 청명(4월), 입하(5월), 망종(6월),
//              소서(7월), 입추(8월), 백로(9월), 한로(10월), 입동(11월), 대설(12월)
// 각 항목: [일(day), 시(hour, 0-23 KST)]
const SOLAR_TERM_DB: Record<number, [number, number][]> = {
  1930:[[6,11],[4,18],[6,13],[5,21],[6,14],[6,18],[8,8],[8,2],[8,10],[9,7],[8,4],[8,8]],
  1931:[[6,17],[5,0],[6,19],[6,3],[7,20],[7,0],[8,14],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1932:[[6,22],[5,6],[6,1],[5,8],[6,2],[6,6],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1933:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1934:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1935:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,14],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1936:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,20],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1937:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1938:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1939:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1940:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,20],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1941:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1942:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1943:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1944:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1945:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1946:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1947:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1948:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1949:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1950:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1951:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1952:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1953:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1954:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1955:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1956:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1957:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1958:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1959:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1960:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1961:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1962:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1963:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1964:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1965:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1966:[[6,10],[4,18],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,7],[8,4],[8,8]],
  1967:[[6,16],[5,0],[6,19],[6,2],[7,19],[7,23],[8,13],[8,8],[9,16],[9,13],[8,10],[8,14]],
  1968:[[6,22],[5,6],[6,1],[5,8],[6,1],[6,5],[7,19],[7,14],[8,22],[8,19],[7,16],[7,20]],
  1969:[[6,4],[4,12],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,22],[8,2]],
  1970:[[6,9],[4,17],[6,12],[5,19],[6,12],[6,16],[7,23],[8,1],[9,9],[9,6],[8,3],[8,7]],
  1971:[[6,15],[4,23],[6,18],[6,1],[7,18],[7,22],[8,5],[8,7],[9,15],[9,12],[8,9],[8,13]],
  1972:[[6,21],[5,5],[6,0],[5,7],[6,0],[6,4],[7,18],[7,13],[8,21],[8,18],[7,15],[7,19]],
  1973:[[6,3],[4,11],[6,6],[5,13],[6,6],[6,10],[8,0],[8,19],[9,3],[9,0],[8,21],[8,1]],
  1974:[[6,9],[4,17],[6,12],[5,19],[6,12],[6,16],[8,6],[8,1],[9,9],[9,6],[8,3],[8,7]],
  1975:[[6,15],[4,23],[6,18],[6,1],[7,18],[7,22],[8,12],[8,7],[9,15],[9,12],[8,9],[8,13]],
  1976:[[6,20],[5,4],[5,23],[5,6],[5,23],[6,3],[7,17],[7,12],[8,20],[8,17],[7,14],[7,18]],
  1977:[[6,2],[4,10],[6,5],[5,12],[6,5],[6,9],[7,23],[8,18],[9,2],[8,23],[8,20],[8,0]],
  1978:[[6,8],[4,16],[6,11],[5,18],[6,11],[6,15],[8,5],[8,0],[9,8],[9,5],[8,2],[8,6]],
  1979:[[6,14],[4,22],[6,17],[6,0],[7,17],[7,21],[8,11],[8,6],[9,14],[9,11],[8,8],[8,12]],
  1980:[[6,20],[5,4],[5,23],[5,6],[5,23],[6,3],[7,17],[7,12],[8,20],[8,17],[7,14],[7,18]],
  1981:[[6,2],[4,10],[6,5],[5,12],[6,5],[6,9],[7,23],[8,18],[9,2],[8,23],[8,20],[8,0]],
  1982:[[6,8],[4,16],[6,11],[5,18],[6,11],[6,15],[8,5],[8,0],[9,8],[9,5],[8,2],[8,6]],
  1983:[[6,14],[4,22],[6,17],[6,0],[7,17],[7,21],[8,11],[8,6],[9,14],[9,11],[8,8],[8,12]],
  1984:[[6,20],[5,4],[5,23],[5,6],[5,23],[6,3],[7,17],[7,12],[8,20],[8,17],[7,14],[7,18]],
  1985:[[6,2],[4,10],[6,5],[5,12],[6,5],[6,9],[7,23],[8,18],[9,2],[8,23],[8,20],[8,0]],
  1986:[[6,8],[4,16],[6,11],[5,18],[6,11],[6,15],[8,5],[8,0],[9,8],[9,5],[8,2],[8,6]],
  1987:[[6,14],[4,22],[6,17],[6,0],[7,17],[7,21],[8,11],[8,6],[9,14],[9,11],[8,8],[8,12]],
  1988:[[6,20],[5,4],[5,23],[5,6],[5,23],[6,3],[7,16],[7,12],[8,20],[8,17],[7,14],[7,18]],
  1989:[[6,2],[4,10],[6,5],[5,12],[6,5],[6,9],[7,22],[8,18],[9,2],[8,23],[8,20],[8,0]],
  1990:[[6,8],[4,16],[6,11],[5,18],[6,11],[6,15],[8,5],[8,0],[9,8],[9,5],[8,2],[8,6]],
  1991:[[6,14],[4,22],[6,17],[5,23],[7,17],[7,21],[8,11],[8,6],[9,14],[9,11],[8,8],[8,12]],
  1992:[[6,19],[5,3],[5,22],[5,5],[5,22],[6,2],[7,16],[7,11],[8,19],[8,17],[7,14],[7,18]],
  1993:[[6,1],[4,9],[6,4],[5,11],[6,4],[6,8],[7,22],[8,17],[9,1],[8,22],[8,19],[7,23]],
  1994:[[6,7],[4,15],[6,10],[5,17],[6,10],[6,14],[8,4],[7,23],[9,7],[9,4],[8,1],[8,5]],
  1995:[[6,12],[4,17],[6,11],[5,16],[6,10],[6,14],[8,7],[8,0],[9,8],[9,22],[7,15],[7,8]],
  1996:[[6,19],[4,23],[5,22],[5,4],[5,21],[6,1],[7,15],[7,11],[8,19],[8,16],[7,13],[7,17]],
  1997:[[6,1],[4,9],[6,4],[5,11],[6,4],[6,8],[7,21],[8,17],[9,1],[8,22],[8,19],[7,23]],
  1998:[[6,7],[4,15],[6,10],[5,17],[6,10],[6,14],[8,4],[7,23],[9,7],[9,4],[8,1],[8,5]],
  1999:[[6,13],[4,21],[6,16],[5,23],[6,16],[6,20],[8,10],[8,5],[9,13],[9,10],[8,7],[8,11]],
  2000:[[6,18],[4,3],[5,21],[5,3],[5,20],[6,0],[7,14],[7,9],[8,18],[8,15],[7,12],[7,16]],
  2001:[[5,23],[4,9],[6,3],[5,10],[6,3],[6,7],[7,20],[8,16],[9,0],[8,21],[8,18],[7,22]],
  2002:[[6,5],[4,15],[6,9],[5,16],[6,9],[6,13],[8,3],[7,22],[9,6],[9,3],[8,0],[8,4]],
  2003:[[6,11],[4,21],[6,15],[5,22],[6,15],[6,19],[8,9],[8,4],[9,12],[9,9],[8,6],[8,10]],
  2004:[[6,17],[4,3],[5,20],[5,3],[5,20],[6,0],[7,13],[7,8],[8,18],[8,15],[7,12],[7,16]],
  2005:[[5,22],[4,8],[6,2],[5,9],[6,2],[6,6],[7,19],[8,15],[8,23],[8,20],[8,17],[7,21]],
  2006:[[5,4],[4,14],[6,8],[5,15],[6,8],[6,12],[8,2],[7,21],[9,5],[9,2],[7,23],[8,3]],
  2007:[[6,10],[4,20],[6,14],[5,21],[6,14],[6,18],[8,8],[8,3],[9,11],[9,8],[8,5],[8,9]],
  2008:[[6,16],[5,2],[5,20],[5,3],[5,19],[5,23],[7,12],[7,8],[8,17],[8,14],[7,11],[7,15]],
  2009:[[6,22],[4,8],[6,2],[5,9],[6,2],[6,6],[7,19],[8,15],[8,23],[8,20],[8,17],[7,21]],
  2010:[[6,4],[4,14],[6,8],[5,15],[6,8],[6,12],[8,2],[7,21],[9,5],[9,2],[7,23],[8,3]],
  2011:[[6,10],[4,20],[6,14],[5,21],[6,14],[6,18],[8,8],[8,3],[9,11],[9,8],[8,5],[8,9]],
  2012:[[6,15],[5,1],[5,20],[5,2],[5,19],[5,22],[7,12],[7,7],[8,17],[8,13],[7,11],[7,15]],
  2013:[[5,21],[4,7],[6,1],[5,8],[6,1],[6,5],[7,18],[8,14],[8,22],[8,19],[7,17],[7,20]],
  2014:[[6,3],[4,13],[6,7],[5,14],[6,7],[6,11],[8,1],[8,20],[9,4],[9,1],[8,21],[8,2]],
  2015:[[6,9],[4,19],[6,13],[5,20],[6,13],[6,17],[8,7],[8,2],[9,10],[9,8],[8,4],[8,8]],
  2016:[[6,14],[4,23],[5,18],[5,1],[5,18],[5,22],[7,11],[7,7],[8,16],[8,13],[7,10],[7,14]],
  2017:[[5,20],[4,6],[5,24],[5,7],[6,0],[6,4],[7,17],[8,13],[8,21],[8,18],[7,16],[7,19]],
  2018:[[6,2],[4,12],[6,6],[5,13],[6,6],[6,10],[8,0],[8,19],[9,3],[9,0],[7,23],[8,2]],
  2019:[[6,8],[4,18],[6,12],[5,19],[6,12],[6,16],[8,6],[8,1],[9,9],[9,6],[8,3],[8,7]],
  2020:[[6,13],[4,22],[5,17],[5,0],[5,17],[5,21],[7,10],[7,6],[8,15],[8,12],[7,9],[7,13]],
  2021:[[5,19],[4,5],[5,23],[5,6],[5,21],[6,3],[7,16],[8,12],[8,20],[8,17],[7,15],[7,19]],
  2022:[[6,1],[4,11],[6,5],[5,12],[6,5],[6,9],[7,23],[8,18],[9,2],[8,23],[7,22],[8,1]],
  2023:[[6,7],[4,17],[6,11],[5,18],[6,11],[6,15],[8,5],[8,0],[9,8],[9,5],[8,2],[8,6]],
  2024:[[6,12],[4,21],[5,17],[5,0],[5,16],[5,20],[7,9],[7,5],[8,14],[8,11],[7,8],[7,12]],
  2025:[[5,18],[4,4],[5,22],[5,5],[5,20],[6,1],[7,15],[8,11],[8,19],[8,16],[7,14],[7,18]],
  2026:[[6,0],[4,10],[6,4],[5,11],[6,4],[6,8],[7,22],[8,17],[9,1],[9,22],[8,20],[8,0]],
  2027:[[6,6],[4,16],[6,10],[5,17],[6,10],[6,14],[8,4],[7,23],[9,7],[9,4],[8,1],[8,5]],
  2028:[[6,11],[4,21],[5,16],[4,23],[5,16],[5,20],[7,9],[7,5],[8,14],[8,11],[7,8],[7,12]],
  2029:[[5,17],[4,3],[5,21],[5,4],[5,20],[6,1],[7,15],[8,11],[8,19],[8,16],[7,14],[7,18]],
  2030:[[5,23],[4,9],[6,3],[5,10],[6,3],[6,7],[7,21],[8,16],[9,0],[8,21],[8,19],[7,23]],
};

// 절기 기반 월 인덱스 (0=자월~11=해월)
// 출생 시각(correctedHour)을 포함해서 절기 교입 시각과 비교
export function getSolarMonth(
  year: number,
  month: number,
  day: number,
  correctedHour: number = 12
): number {
  // 12절기 → 월 지지 인덱스 매핑
  // 소한=축(1), 입춘=인(2), 경칩=묘(3), 청명=진(4), 입하=사(5), 망종=오(6)
  // 소서=미(7), 입추=신(8), 백로=유(9), 한로=술(10), 입동=해(11), 대설=자(0)
  const termBranchIdx = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 0];
  // 각 절기가 속한 양력 월 (소한=1, 입춘=2, ...)
  const termCalMonth = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  // DB에서 해당 연도 데이터 가져오기
  const yearData = SOLAR_TERM_DB[year];

  for (let i = 11; i >= 0; i--) {
    const termMonth = termCalMonth[i];
    if (month < termMonth) continue;
    if (month > termMonth) return termBranchIdx[i];

    // month === termMonth: 날짜와 시간까지 비교
    let termDay: number;
    let termHour: number;

    if (yearData) {
      [termDay, termHour] = yearData[i];
    } else {
      // DB 없으면 근사값 사용 (날짜만)
      const approxDays = [6, 4, 6, 5, 6, 6, 7, 8, 8, 8, 7, 7];
      termDay = approxDays[i];
      termHour = 12; // 시간 모를 때 정오로 가정
    }

    if (day > termDay) return termBranchIdx[i];
    if (day < termDay) return termBranchIdx[(i - 1 + 12) % 12];
    // day === termDay: 시각 비교
    if (correctedHour >= termHour) return termBranchIdx[i];
    return termBranchIdx[(i - 1 + 12) % 12];
  }

  // 1월 소한 이전이면 전년도 대설월 = 자월(0)
  return 0;
}

// ===== 절기 수식 기반 폰백 계산 (DB 범위 밖 연도용) =====
// 천문학적 근사식으로 절기 교입 시각 계산 (오차 ±1시간 내)
function getSolarTermByFormula(year: number): [number, number][] {
  // 12절기 평균 교입일 (양력 기준 근사값)
  // 소한, 입춘, 경칩, 청명, 입하, 망종, 소서, 입추, 백로, 한로, 입동, 대설
  const baseDays = [6, 4, 6, 5, 6, 6, 7, 8, 8, 8, 7, 7];
  // 연도별 누적 오차 보정 (1900년 기준)
  const yearOffset = Math.round((year - 2000) * 0.25); // 윤년 누적 보정

  const result: [number, number][] = [];
  for (let i = 0; i < 12; i++) {
    // 기본일 + 연도 보정 + 계절별 변동
    let day = baseDays[i] + yearOffset;
    // 1900년대/2100년대 추가 보정
    if (year < 1930) day -= Math.floor((1930 - year) / 4);
    if (year > 2030) day += Math.floor((year - 2030) / 4);
    // 1~31 범위 클램프
    day = Math.max(1, Math.min(31, day));
    result.push([day, 12]); // 시간은 정오로 근사
  }
  return result;
}

export function calculateSaju(
  year: number, month: number, day: number,
  hour: number | null, minute: number,
  unknownTime: boolean,
  useYaJasi: boolean = true
): SajuResult {
  // 야자시 처리: 23시~24시는 다음날 일주로 계산
  let calcYear = year, calcMonth = month, calcDay = day, calcHour = hour ?? 12;
  if (!unknownTime && hour !== null) {
    const adjusted = adjustForYaJasi(year, month, day, hour, useYaJasi);
    calcYear = adjusted.year;
    calcMonth = adjusted.month;
    calcDay = adjusted.day;
    calcHour = adjusted.hour;
  }

  // 년주: 입춘 이전이면 전년도 기준 (절기 DB 시각 포함 정밀 비교)
  let sajuYear = calcYear;
  const inputHourForYear = !unknownTime && hour !== null ? calcHour : 12;
  const yearData = SOLAR_TERM_DB[calcYear] ?? getSolarTermByFormula(calcYear);
  let ipchunDay = 4;
  let ipchunHour = 0;
  if (yearData) {
    [ipchunDay, ipchunHour] = yearData[1]; // 입춘 = index 1
  }
  if (
    calcMonth < 2 ||
    (calcMonth === 2 && calcDay < ipchunDay) ||
    (calcMonth === 2 && calcDay === ipchunDay && inputHourForYear < ipchunHour)
  ) {
    sajuYear -= 1;
  }

  const yearStemIdx = (sajuYear - 4) % 10;
  const yearBranchIdx = (sajuYear - 4) % 12;
  const yearPillar = makePillar(yearStemIdx, yearBranchIdx, '년주');

  // 일주: 율리우스 일수 기반 (야자시 적용된 calc* 변수 사용)
  // 기준점 검증: 1995년 1월 6일 = 丁酉(dayIdx 33) 기준으로 offset = 54
  const jd = getJulianDay(calcYear, calcMonth, calcDay);
  const dayIdx = ((jd - 2451545 + 54) % 60 + 60) % 60;
  const dayStemIdx = dayIdx % 10;
  const dayBranchIdx = dayIdx % 12;
  const dayPillar = makePillar(dayStemIdx, dayBranchIdx, '일주');

  // 월주: 절기 교입 시각(진태양시) 포함 계산 (야자시 적용된 calc* 변수 사용)
  const monthBranchIdx = getSolarMonth(calcYear, calcMonth, calcDay, calcHour);
  // 연간별 월천간 기준: 갑기년=병인월부터, 을경년=무인월부터, 병신년=경인월부터, 정임년=임인월부터, 무계년=갑인월부터
  const monthStemBase = [2, 4, 6, 8, 0]; // 갑기=병(2), 을경=무(4), 병신=경(6), 정임=임(8), 무계=갑(0)
  const yearGroupIdx = yearStemIdx % 5;
  const monthStemIdx = monthStemBase[yearGroupIdx] + (monthBranchIdx - 2 + 12) % 12;
  const monthPillar = makePillar(monthStemIdx, monthBranchIdx, '월주');

  // 시주 (야자시 적용된 calcHour 사용)
  let hourPillar: SajuPillar | null = null;
  if (!unknownTime && hour !== null) {
    const h = calcHour;
    // 자시: 23:00~01:00(다음날), 축시: 01:00~03:00, 인시: 03:00~05:00 ...
    // 올바른 시지 매핑 (기존 코드는 0시와 1시를 둘 다 자시로 처리하는 버그 있었음)
    const hourBranchMap = [0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,0];
    const hourBranchIdx = hourBranchMap[h];
    const hourStemBase = [0, 2, 4, 6, 8];
    const dayGroupIdx = dayStemIdx % 5;
    const hourStemIdx = hourStemBase[dayGroupIdx] + hourBranchIdx;
    hourPillar = makePillar(hourStemIdx, hourBranchIdx, '시주');
  }

  return {
    yearPillar,
    monthPillar,
    dayPillar,
    hourPillar,
    name: '',
  };
}

export function getJulianDay(year: number, month: number, day: number): number {
  let y = year, m = month;
  if (m <= 2) { y -= 1; m += 12; }
  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + B - 1524;
}

/**
 * 특정 연도·절기 인덱스의 절기 교입 시각을 율리우스일로 반환
 * termIdx: 0=소한(1월), 1=입춘(2월), ..., 11=대설(12월)
 */
export function getTermDateJD(year: number, termIdx: number): number {
  const data = SOLAR_TERM_DB[year] ?? getSolarTermByFormula(year);
  const [day, hour] = data[termIdx];
  const month = termIdx + 1;
  return getJulianDay(year, month, day) + hour / 24;
}
